import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { getResolution } from 'web/src/utilities/resolution';
import { getUSDAProductStatusTypes } from '..';
import { USDAProductStatusTypeData } from '../../api';
import { ChatMessage, ChatRequest, EvaluateChatRequest, getChatTimestamp } from '../../api/models/chat.models';
import { CustomerProductPriceResult } from '../../api/models/customer-product-price.models';
import ChatService from '../../api/services/chat.service';
import { AnalyticsContext, generateId, getAnalyticsItemData, getFilteredProductPriceRequests } from '../../helpers';
import { useAppInsightsLogger } from '../../logging/AppInsightsLogger';
import { UserActivityAction, UserActivityActionSummary, UserActivityPageName } from '../../models';
import { AppDispatch, AppThunk, RootState } from '../store';
import { chatSlice, selectAllChatProducts, selectChatUsdaProducts } from './chat.slice';
import { getOrderEntryCustomerProductPrices } from './customer.thunks';
import { globalSlice } from './global.slice';
import { logUserActivity } from './user-activity.thunks';
import { selectUserPermissionSetting } from './user.slice';

// hooks
const appInsightsLogger = useAppInsightsLogger();
const chatService = ChatService.getInstance();

export const resetChat = (): AppThunk => async (dispatch) => {
  chatService.abortGetChat();
  dispatch(chatSlice.actions.resetState());
};

export const getChat =
  (id: string, request: ChatRequest, analyticsContext?: AnalyticsContext): AppThunk<Promise<ChatMessage | undefined>> =>
  async (dispatch: AppDispatch, getState) => {
    var thunkResult: ChatMessage | undefined = undefined;
    try {
      const userMessage: ChatMessage = {
        id: generateId(),
        prompt: '',
        message: request.UserPrompt,
        source: 'user',
        productIds: [],
        timestamp: getChatTimestamp(),
      };
      dispatch(chatSlice.actions.setChatMessage(userMessage));
      dispatch(chatSlice.actions.setIsLoading(true));

      const { data } = await chatService.getChat(request);
      if (data.IsSuccess && getState().chat.visible) {
        const result = data.ResultObject;

        const systemMessage: ChatMessage = {
          id: id,
          prompt: request.UserPrompt,
          message: result.Message,
          productIds: result.Products.map((p) => p.Id),
          source: 'system',
          timestamp: getChatTimestamp(),
          productCatalogConversationId: data.ResultObject.ProductCatalogConversationId,
        };

        thunkResult = systemMessage;

        dispatch(chatSlice.actions.setProducts(result.Products));
        dispatch(chatSlice.actions.setThreadId(result.ThreadId));
        dispatch(chatSlice.actions.setChatMessage(systemMessage));

        // if (messageCount <= 1) {
        //   const fmsg = getChatFollowUpMessage(result.Products.length);
        //   dispatch(chatSlice.actions.setChatMessage(fmsg));
        // }

        dispatch(getChatPrices());
        dispatch(getChatUsdaProductStatusTypes());

        analyticsContext?.analytics?.viewItemList?.(
          data.ResultObject.Products.flatMap((p, index) =>
            p.UnitOfMeasureOrderQuantities.map((uom) =>
              getAnalyticsItemData(
                p.ProductKey ?? '',
                uom.UnitOfMeasure.toString(),
                uom.Price ?? 0,
                uom.Quantity,
                index,
                uom,
                p,
                analyticsContext.listId,
                analyticsContext.listName
              )
            )
          ),
          analyticsContext.listId,
          analyticsContext.listName
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    }

    dispatch(chatSlice.actions.setIsLoading(false));

    dispatch(
      logUserActivity({
        action: !request.ThreadId
          ? UserActivityAction.CreateSageConversation
          : UserActivityAction.CreateSageConversationMessage,
        pageName: UserActivityPageName.SageConversation,
        actionSummary: !request.ThreadId
          ? UserActivityActionSummary.CreateSageConversation
          : UserActivityActionSummary.CreateSageConversationMessage,
        resolution: getResolution(),
      })
    );

    return thunkResult;
  };

export const startChatThread = (): AppThunk => async (dispatch, getState) => {
  const userMessage: ChatMessage = {
    id: generateId(),
    prompt: '',
    message: 'New search thread',
    source: 'thread',
    productIds: [],
    timestamp: getChatTimestamp(),
  };
  dispatch(chatSlice.actions.setChatMessage(userMessage));
  dispatch(chatSlice.actions.setThreadId(undefined));
};

export const getPrompts = (): AppThunk => async (dispatch: AppDispatch, getState: () => RootState) => {
  try {
    if (!getState().chat.isLoadingPrompts) {
      dispatch(chatSlice.actions.setIsLoadingPrompts(true));
    }

    const { data } = await chatService.getPrompts();

    if (data.IsSuccess) {
      dispatch(chatSlice.actions.setPrompts(data.ResultObject));
    }
  } catch (error) {
    appInsightsLogger.trackException({
      exception: error,
      severityLevel: SeverityLevel.Error,
    });
  } finally {
    dispatch(chatSlice.actions.setIsLoadingPrompts(false));
  }
};

export const evaluateChat =
  (msg: ChatMessage, request: EvaluateChatRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(chatSlice.actions.updateChatMessage({ id: msg.id, response: request.ResponseTypeEnum }));

      const { data } = await chatService.evaluateChat(request);
      if (!data.IsSuccess) {
        dispatch(chatSlice.actions.updateChatMessage({ id: msg.id, response: msg.response }));
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    }

    dispatch(
      logUserActivity({
        action: UserActivityAction.UpdateSageConversationMessage,
        pageName: UserActivityPageName.SageConversation,
        actionSummary: UserActivityActionSummary.UpdateSageConversationMessage,
        resolution: getResolution(),
      })
    );
  };

export const getChatPrices = (): AppThunk => async (dispatch: AppDispatch, getState: () => RootState) => {
  const canViewPrice = selectUserPermissionSetting(getState()).CanViewPrices;
  const catalogProducts = selectAllChatProducts(getState().chat.products);
  const filteredSet = getFilteredProductPriceRequests(catalogProducts);
  const apiRequest = getState().chat.threadId;

  if (!canViewPrice) return;
  if (filteredSet.length <= 0) return;

  while (filteredSet.length > 0) {
    // sanity check: don't process stale data
    if (apiRequest !== getState().chat.threadId) {
      break;
    }

    await dispatch(
      getOrderEntryCustomerProductPrices(filteredSet.splice(0, 142), (priceResults: CustomerProductPriceResult[]) => {
        dispatch(chatSlice.actions.setProductPrices(priceResults));
      })
    );
  }
};

export const getChatUsdaProductStatusTypes = (): AppThunk => (dispatch: AppDispatch, getState: () => RootState) => {
  const productKeys = selectChatUsdaProducts(getState().chat.products);
  const customerId = getState().customer.selectedCustomer?.CustomerId ?? '';
  dispatch(
    getUSDAProductStatusTypes(customerId, productKeys, (statusResults: USDAProductStatusTypeData[]) => {
      dispatch(chatSlice.actions.setUsdaProductStatusType(statusResults));
    })
  );
};
